import { t } from "../i18n";
import type { PlanDesignComponent } from "../api/types";

export function resizedPlanScale(startScale: number, startDistance: number, distance: number): number {
  const ratio = Math.max(1, distance) / Math.max(1, startDistance);
  return Math.round(Math.max(0.1, Math.min(4, startScale * ratio)) * 100) / 100;
}

/** One visible, keyboard-accessible handle; updates the existing component scale. */
export function attachPlanResizeHandle(
  frame: HTMLElement, image: HTMLImageElement, component: PlanDesignComponent,
  update: () => void, finish: () => void,
): HTMLButtonElement {
  frame.querySelectorAll("[data-plan-resize]").forEach((item) => item.remove());
  const handle = document.createElement("button");
  handle.type = "button";
  handle.className = "city-plan-resize-handle";
  handle.dataset.planResize = component.id;
  handle.textContent = "⤡";
  handle.title = "این گوشه را بکشید تا اَسِت بزرگ یا کوچک شود";
  handle.setAttribute("aria-label", "تغییر اندازه اَسِت؛ گوشه را بکشید یا از کلیدهای جهت استفاده کنید");
  const position = () => {
    const bounds = frame.getBoundingClientRect();
    const rect = image.getBoundingClientRect();
    handle.style.left = `${rect.right - bounds.left}px`;
    handle.style.top = `${rect.bottom - bounds.top}px`;
  };
  image.addEventListener("load", position);
  frame.appendChild(handle);
  position();
  handle.addEventListener("click", (event) => event.stopPropagation());
  handle.addEventListener("keydown", (event) => {
    const direction = ["ArrowRight", "ArrowUp"].includes(event.key) ? 1
      : ["ArrowLeft", "ArrowDown"].includes(event.key) ? -1 : 0;
    if (!direction && event.key !== "Home") return;
    event.preventDefault();
    event.stopPropagation();
    component.scale = event.key === "Home" ? 1
      : Math.round(Math.max(0.1, Math.min(4, component.scale + direction * 0.1)) * 100) / 100;
    update(); position();
    handle.setAttribute("aria-label", `${t("Resize asset; drag the corner or use the arrow keys", "تغییر اندازه اَسِت؛ گوشه را بکشید یا از کلیدهای جهت استفاده کنید")} · ${component.scale}`);
  });
  handle.addEventListener("pointerdown", (event) => {
    event.preventDefault(); event.stopPropagation();
    const rect = image.getBoundingClientRect();
    const origin = getComputedStyle(image).transformOrigin.split(" ").map(Number.parseFloat);
    const bounds = frame.getBoundingClientRect();
    const style = getComputedStyle(image);
    // The image's transform pivot is its placement point, including its visual offset.
    const matrix = new DOMMatrixReadOnly(style.transform);
    const left = image.offsetLeft;
    const top = image.offsetTop;
    const pivot = {
      x: bounds.left + left + (origin[0] || 0) + matrix.e,
      y: bounds.top + top + (origin[1] || 0) + matrix.f,
    };
    if (!rect.width || !rect.height) return;
    const start = Math.hypot(event.clientX - pivot.x, event.clientY - pivot.y);
    const scale = component.scale;
    handle.setPointerCapture(event.pointerId);
    handle.classList.add("is-resizing");
    const move = (next: PointerEvent) => {
      if (next.pointerId !== event.pointerId) return;
      component.scale = resizedPlanScale(scale, start, Math.hypot(next.clientX - pivot.x, next.clientY - pivot.y));
      update(); position();
    };
    const stop = (next: PointerEvent) => {
      if (next.pointerId !== event.pointerId) return;
      handle.removeEventListener("pointermove", move);
      handle.removeEventListener("pointerup", stop);
      handle.removeEventListener("pointercancel", stop);
      handle.removeEventListener("lostpointercapture", stop);
      if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
      finish();
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", stop);
    handle.addEventListener("pointercancel", stop);
    handle.addEventListener("lostpointercapture", stop);
  });
  return handle;
}
